// Empire Apps Chassis · identity-zkp · BBS+ adapter
// Full BBS+ signatures on BLS12-381 are the W3C-standardised primitive for
// selective disclosure (CFRG bbs-signatures draft + W3C VC-2.0 BBS-2023 suite).
// Hand-rolling BLS12-381 pairing in browser-vanilla TS is a ~3 month project
// (Miller loop, final exponentiation, towering 1/2/6/12, hash-to-curve).
//
// v0 strategy (this file): ship the public API surface as a thin adapter over
// the Hash-Commitment + Ed25519-Attest path (commitment.ts + selective-disclosure.ts).
// Any caller that uses the BBS+ API today gets working selective disclosure with
// hash commitments + per-claim Ed25519 signatures from the issuer, which is
// cryptographically sound but not pairing-elegant. The proof size is larger
// than a real BBS+ proof but identical security for our threat model
// (issuer attests claims independently, verifier checks subset).
//
// v1.1: replace the body with real BLS12-381 once @noble/bls12-381 is allowed.
// The adapter contract below is forward-compatible.

import type {
  BBSProof,
  Predicate,
  AttributeValue,
  Ed25519PublicKey,
  Ed25519PrivateKey,
} from './types.ts';
import { commit, newBlindingFactor, toHex, fromHex, serialise } from './commitment.ts';
import { ed25519Sign, ed25519Verify, base64UrlDecode, base64UrlEncode } from './did.ts';

export interface BBSSignedCredential {
  readonly attributes: Record<string, AttributeValue>;
  readonly commitments: Record<string, string>; // attribute -> commitment hex
  readonly blindings: Record<string, string>; // attribute -> blinding hex (kept by holder)
  readonly attestations: Record<string, string>; // attribute -> Ed25519 sig hex over commitment
  readonly issuerDidKeyHex: string; // Ed25519 public key hex
  readonly signedAt: string;
}

export interface BBSDerivedProof {
  readonly disclosedAttributes: Record<string, AttributeValue>;
  readonly disclosedBlindings: Record<string, string>;
  readonly disclosedCommitments: Record<string, string>;
  readonly disclosedAttestations: Record<string, string>;
  readonly hiddenCommitments: Record<string, string>;
  readonly hiddenAttestations: Record<string, string>;
  readonly issuerDidKeyHex: string;
  readonly proofGeneratedAt: string;
}

export async function bbsSign(
  attributes: Record<string, AttributeValue>,
  issuerPrivateKey: Ed25519PrivateKey,
  issuerPublicKey: Ed25519PublicKey,
): Promise<BBSSignedCredential> {
  const commitments: Record<string, string> = {};
  const blindings: Record<string, string> = {};
  const attestations: Record<string, string> = {};
  for (const [attr, val] of Object.entries(attributes)) {
    const bf = newBlindingFactor();
    const c = await commit(attr, val, bf);
    commitments[attr] = c;
    blindings[attr] = toHex(bf);
    const msg = new TextEncoder().encode(`${attr}|${c}`);
    const sig = await ed25519Sign(msg, issuerPrivateKey);
    attestations[attr] = toHex(sig);
  }
  return {
    attributes,
    commitments,
    blindings,
    attestations,
    issuerDidKeyHex: toHex(issuerPublicKey),
    signedAt: new Date().toISOString(),
  };
}

export async function bbsDerive(
  cred: BBSSignedCredential,
  reveal: readonly string[],
): Promise<BBSDerivedProof> {
  const disclosedAttributes: Record<string, AttributeValue> = {};
  const disclosedBlindings: Record<string, string> = {};
  const disclosedCommitments: Record<string, string> = {};
  const disclosedAttestations: Record<string, string> = {};
  const hiddenCommitments: Record<string, string> = {};
  const hiddenAttestations: Record<string, string> = {};
  for (const [attr, val] of Object.entries(cred.attributes)) {
    if (reveal.includes(attr)) {
      disclosedAttributes[attr] = val;
      disclosedBlindings[attr] = cred.blindings[attr] as string;
      disclosedCommitments[attr] = cred.commitments[attr] as string;
      disclosedAttestations[attr] = cred.attestations[attr] as string;
    } else {
      hiddenCommitments[attr] = cred.commitments[attr] as string;
      hiddenAttestations[attr] = cred.attestations[attr] as string;
    }
  }
  return {
    disclosedAttributes,
    disclosedBlindings,
    disclosedCommitments,
    disclosedAttestations,
    hiddenCommitments,
    hiddenAttestations,
    issuerDidKeyHex: cred.issuerDidKeyHex,
    proofGeneratedAt: new Date().toISOString(),
  };
}

export async function bbsVerifyDerived(
  proof: BBSDerivedProof,
): Promise<{ ok: boolean; reason: string }> {
  const issuerPk = fromHex(proof.issuerDidKeyHex) as Ed25519PublicKey;
  for (const attr of Object.keys(proof.disclosedAttributes)) {
    const c = proof.disclosedCommitments[attr];
    const bf = proof.disclosedBlindings[attr];
    const sig = proof.disclosedAttestations[attr];
    const val = proof.disclosedAttributes[attr] as AttributeValue;
    if (!c || !bf || !sig) return { ok: false, reason: 'missing-disclosed-fields' };
    const recomputed = await commit(attr, val, fromHex(bf) as BlindingMarker);
    if (recomputed !== c) return { ok: false, reason: 'commitment-mismatch' };
    const msg = new TextEncoder().encode(`${attr}|${c}`);
    const ok = await ed25519Verify(msg, fromHex(sig) as Ed25519SigMarker, issuerPk);
    if (!ok) return { ok: false, reason: 'attestation-invalid' };
  }
  for (const attr of Object.keys(proof.hiddenCommitments)) {
    const c = proof.hiddenCommitments[attr];
    const sig = proof.hiddenAttestations[attr];
    if (!c || !sig) return { ok: false, reason: 'missing-hidden-fields' };
    const msg = new TextEncoder().encode(`${attr}|${c}`);
    const ok = await ed25519Verify(msg, fromHex(sig) as Ed25519SigMarker, issuerPk);
    if (!ok) return { ok: false, reason: 'hidden-attestation-invalid' };
  }
  return { ok: true, reason: 'verified' };
}

export function bbsProofToHex(proof: BBSDerivedProof): BBSProof {
  return base64UrlEncode(new TextEncoder().encode(JSON.stringify(proof))) as BBSProof;
}

export function bbsProofFromHex(hex: BBSProof): BBSDerivedProof {
  return JSON.parse(new TextDecoder().decode(base64UrlDecode(hex))) as BBSDerivedProof;
}

// internal type markers — narrow re-imports to keep file self-contained
type BlindingMarker = Uint8Array & { readonly __zkpBrand: 'BlindingFactor' };
type Ed25519SigMarker = Uint8Array & { readonly __zkpBrand: 'Ed25519Signature' };
